import { ErrorBanner } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { LoadingSpinner, TextArea, Text, Select, Dialog } from '@soteria/design-system-ve';
import type { StandardLabel } from '@system-config/label-management/domain';
import { useLabelPolicyContent, useUpdateLabelPolicyContent } from '@system-config/label-management/hooks';
import { getBackupFileLabel, sortBackupVersions } from '@system-config/label-management/utils';
import { useEffect, useState } from 'react';

import * as styles from './LabelPolicyDialog.css';

interface LabelPolicyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  label?: StandardLabel;
}

// eslint-disable-next-line complexity
function LabelPolicyDialogComponent({ isOpen, onClose, label }: LabelPolicyDialogProps) {
  const [content, setContent] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<string>('current');

  // Use the policy content hook with selected filename (undefined for current version)
  const selectedFilename = selectedVersion === 'current' ? undefined : selectedVersion;
  const { policyContent, loading, errors: queryErrors } = useLabelPolicyContent(label?.id || '', selectedFilename);
  const { updateLabelPolicyContent, loading: updating, errors: updateErrors, resetErrors } = useUpdateLabelPolicyContent();

  // Always get current version metadata for last modified date
  const { policyContent: currentPolicyContent } = useLabelPolicyContent(label?.id || '');

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetErrors();
      setSelectedVersion('current');
    }
  }, [isOpen, resetErrors]);

  // Update content when policy content changes
  useEffect(() => {
    if (policyContent?.content !== undefined) {
      setContent(policyContent.content);
    }
  }, [policyContent?.content]);

  // Handle content changes
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  // Handle save
  const handleSave = async () => {
    resetErrors();

    if (!label) return;
    const success = await updateLabelPolicyContent(label.id, content);

    if (success) {
      onClose();
    }
  };

  // Handle version selection from dropdown
  const handleVersionChange = (value: string) => {
    setSelectedVersion(value);
  };

  // Create dropdown options with sorted backup versions (most recent first)
  const sortedBackupVersions = sortBackupVersions(label?.backupVersions || []);

  const versionOptions = [
    { value: 'current', label: 'Current' },
    ...sortedBackupVersions.map(filename => ({
      value: filename,
      label: getBackupFileLabel(filename),
    })),
  ];

  // Format the last modified date (always use current version's date)
  const formattedDate = currentPolicyContent?.lastModified ? new Date(currentPolicyContent.lastModified).toLocaleString() : '';

  // Combine errors from both hooks
  const hasErrors = (queryErrors && queryErrors.length > 0) || (updateErrors && updateErrors.length > 0);
  const allErrors = [...(queryErrors || []), ...(updateErrors || [])];

  return (
    <Dialog
      cancelAction={{ label: 'Close' }}
      description={formattedDate ? `Last Modified: ${formattedDate}` : 'View and edit the policy file content'}
      isOpen={isOpen}
      primaryAction={
        label && !loading
          ? {
              label: updating ? <LoadingSpinner /> : 'Save Changes',
              onClick: handleSave,
              disabled: updating || loading || selectedVersion !== 'current',
            }
          : undefined
      }
      size="xl"
      title={`${label?.labelName || 'Label'} - Policy File Content`}
      onClose={() => {
        // Reset error state when dialog closes
        resetErrors();
        onClose();
      }}
    >
      <div className={styles.form}>
        {/* Error Banner */}
        {hasErrors && <ErrorBanner errors={allErrors} title="There was an error with the policy content:" />}

        {loading ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
          </div>
        ) : (
          <div>
            <div className={styles.fieldContainer}>
              <Text as="div" className={styles.fieldLabel}>
                Version:
              </Text>
              <Select options={versionOptions} value={selectedVersion} onChange={handleVersionChange} />
            </div>

            <div className={styles.horizontalFieldContainer}>
              <Text as="span" className={styles.fieldLabel}>
                File Name:
              </Text>
              <Text as="span">{selectedVersion === 'current' ? label?.policy || 'Policy File' : selectedVersion}</Text>
            </div>

            <TextArea
              placeholder="Policy content will appear here"
              readOnly={selectedVersion !== 'current'}
              size={{ height: 400 }}
              value={content}
              onChange={handleChange}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}

export const LabelPolicyDialog = withAppInsightsTracking(LabelPolicyDialogComponent, 'LabelPolicyDialog');
